import {useMemo} from 'react'
import {Empty, Flex, Space, Table, Tag, Typography} from 'antd'
import {DeleteOutlined, EditOutlined} from '@ant-design/icons'
import Button from '../../common/Button'
import EllipsisTooltip from '../../common/EllipsisTooltip'
import RecordsCount from '../../common/RecordsCount'
import {useIntl} from 'react-intl'
import {formatEmptyValue} from '../../../utils/common'
import TableActions from '../../common/TableActions'

const FunctionalDiagramTable = ({
  dataSource,
  pagination = false,
  getFunctionalDiagramList = null,
  onEdit = () => {},
  onDelete = () => {},
}) => {
  const intl = useIntl()

  const columns = useMemo(
    () => [
      {
        title: intl.formatMessage({ id: 'functionalDiagram.subSystem' }),
        dataIndex: 'sub_system_id',
        width: '27%',
        render: (_, record) => (
          <EllipsisTooltip
            className="ellipsis"
            lineClamp={2}
          >
            <div className="bolder">
              {formatEmptyValue(record?.sub_system_id)}
            </div>
            <div className="text-xs">{formatEmptyValue(record?.name)}</div>
          </EllipsisTooltip>
        ),
      },
      {
        title: intl.formatMessage({ id: 'functionalDiagram.system' }),
        dataIndex: 'system_data',
        width: '27%',
        render: (_, record) => (
          <EllipsisTooltip
            className="ellipsis"
            lineClamp={2}
          >
            <div className="bolder">
              {formatEmptyValue(record?.system_data?.system_id)}
            </div>
            <div className="text-xs">
              {formatEmptyValue(record?.system_data?.name)}
            </div>
          </EllipsisTooltip>
        ),
      },
      {
        title: intl.formatMessage({ id: 'functionalDiagram.salesCode' }),
        dataIndex: 'sales_code_data',
        render: (_, record) =>
          record?.sales_code_data?.length ? (
            <Flex gap={8} wrap="wrap">
              {record.sales_code_data.map((code) => (
                <Tag key={code.id}>{code.name}</Tag>
              ))}
            </Flex>
          ) : (
            '-'
          ),
      },
      {
        title: intl.formatMessage({ id: 'generic.actions' }),
        align: 'right',
        width: 136,
        render: (_, record) => (
          <TableActions
            actions={[
              { key: 'edit', label: intl.formatMessage({id: 'forms.edit'}), icon: <EditOutlined />, onClick: () => onEdit(record) },
              { key: 'delete', label: intl.formatMessage({id: 'forms.delete'}), icon: <DeleteOutlined />, danger: true, onClick: () => onDelete(record) },
            ]}
          />
        ),
      },
    ],
    [intl, onEdit, onDelete]
  )

  return (
    <Space
      direction="vertical"
      size="middle"
      style={{ width: '100%', verticalAlign: 'top' }}
    >
      <Flex style={{ width: '100%' }}>
        <Table
          rowKey={(record) => `functional-diagram-${record.id}`}
          columns={columns}
          dataSource={dataSource}
          showSorterTooltip={false}
          className='functional-diagram-table'
          // tableLayout='fixed'
          scroll={dataSource?.length ? { x: 650 } : undefined}
          pagination={
            pagination
              ? {
                  current: pagination?.currentPage,
                  total: pagination?.totalCount,
                  pageSize: 10,
                  showSizeChanger: false,
                  hideOnSinglePage: true,
                  showTotal: (total) => <RecordsCount count={total} />,
                  onChange: (page) => {
                    const offset = (page - 1) * 10
                    const url = pagination?.nextPageLink
                      ? new URL(pagination?.nextPageLink)
                      : new URL(pagination?.prevPageLink)
                    url.searchParams.set('offset', offset)
                    getFunctionalDiagramList?.(url.toString())
                  },
                }
              : false
          }
          locale={{
            emptyText: (
              <div style={{ padding: '40px' }}>
                <Empty description={<RecordsCount count={0} />} />
              </div>
            ),
          }}
        />
      </Flex>
      {pagination && pagination?.totalPages <= 1 && pagination?.totalCount > 0 && (
        <Typography.Text>
          <RecordsCount count={pagination?.totalCount} />
        </Typography.Text>
      )}
    </Space>
  )
}

export default FunctionalDiagramTable
